import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { AudioPicker } from "@/features/audio-picker";
import { ApiError } from "@/lib/api";
import { bodySummary } from "@/lib/cadence";
import {
  DIALING_BOUNDS, DIAL_MODES, MAX_ABANDON_AFTER_SECONDS, MIN_DIAL_TIMEOUT_SECONDS, NEEDS_CADENCE,
  SAFE_HARBOR_ACTIONS,
  US_TIMEZONES, callerIdChoices, dialModeText, dialingChanges, dialingValues, mediaProblem,
  moveItem, needsCadence, safeHarborText, statusText, windowNote, windowProblem,
  type DialingValues,
} from "@/lib/campaign";
import { zodResolver } from "@/lib/form";
import {
  useCadences, useDids, useGateways, useUpdateCampaign, type CampaignSettingsInput,
} from "@/lib/queries";
import type { Campaign, Gateway } from "@/lib/types";
import { formatPhoneNumber } from "@/lib/utils";

/**
 * How the dialer works the campaign's lists (ADR-019, ADR-067): the cadence it runs, the mode, the
 * ring, the hours, what an answered lead hears when no rep is free, and what the calls present and
 * ride.
 *
 * The cadence is the only dialing policy (ADR-067). How many calls a lead gets, the waits between
 * them and the cap on calls to one number in a day are the cadence's, so the tab has no attempt or
 * redial settings of its own, and a campaign that dials with no cadence is refused: by the form on
 * the field, before the server's 422 says the same.
 *
 * The compliance numbers carry their reasons on the form, because each is a legal floor or window
 * rather than a preference: the dial timeout cannot go under the TSR's 15-second ring, the abandon
 * clock is the two-second safe harbor, and hours outside 8 am to 9 pm on the lead's clock are
 * flagged. core-api refuses what it must; the form says why first.
 *
 * The caller ID is a picker over the tenant's own numbers, never a text field, for the reason the
 * domain's default is: core-api refuses anything else. One Save covers the tab, and it sends only
 * what changed (`dialingChanges`).
 */

/** Radix's Select cannot hold `""`, so "none" — no cadence, a zone left to the fallback — needs a
 *  value of its own. */
const NONE = "none";
/** `outbound.OUTBOUND_PROFILE` in `platform`: the sofia profile a trunk must be on to carry a
 *  dialer call. core-api refuses any other. */
const OUTBOUND_PROFILE = "external";
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

function whole([min, max]: readonly [number, number], unit: string, below?: string) {
  return z
    .number({ error: `Enter a number of ${unit}` })
    .int(`Whole ${unit} only`)
    .min(min, below ?? `At least ${min} ${unit}`)
    .max(max, `At most ${max.toLocaleString()} ${unit}`);
}

const schema = z
  .object({
    cadence_id: z.number().nullable(),
    dial_mode: z.string(),
    // Not `whole()`: a rank is not a count, so "a number of priority" would read wrongly.
    dial_priority: z
      .number({ error: "Enter a priority" })
      .int("Whole numbers only")
      .min(DIALING_BOUNDS.dial_priority[0], "The lowest priority is 0")
      .max(DIALING_BOUNDS.dial_priority[1], "The highest priority is 100"),
    dial_timeout_seconds: whole(
      DIALING_BOUNDS.dial_timeout_seconds,
      "seconds",
      `At least ${MIN_DIAL_TIMEOUT_SECONDS} seconds: an unanswered call rings that long, or four rings, before the dialer may hang it up`,
    ),
    preview_timeout_seconds: whole(DIALING_BOUNDS.preview_timeout_seconds, "seconds"),
    calling_window_start: z.string().regex(HHMM, "A time on a 24-hour clock"),
    calling_window_end: z.string().regex(HHMM, "A time on a 24-hour clock"),
    respect_lead_timezone: z.boolean(),
    default_timezone: z.string(),
    // Not `whole()`: its ceiling message would not say why the ceiling is two (ACL-237).
    abandon_after_seconds: z
      .number({ error: "Enter a number of seconds" })
      .int("Whole seconds only")
      .min(DIALING_BOUNDS.abandon_after_seconds[0], "At least 1 seconds")
      .max(
        MAX_ABANDON_AFTER_SECONDS,
        `At most ${MAX_ABANDON_AFTER_SECONDS} seconds: a lead who answered may wait that long for a rep before the call counts as abandoned`,
      ),
    safe_harbor_action: z.string(),
    safe_harbor_media: z
      .string()
      .refine((v) => mediaProblem(v) === null, "Choose a file from the audio store"),
    outbound_gateway_ids: z.array(z.number()),
    caller_id_number: z.string(),
    rep_number_caller_id: z.boolean(),
    list_horizon_days: whole(DIALING_BOUNDS.list_horizon_days, "days"),
  })
  .refine((v) => v.safe_harbor_action !== "announcement" || v.safe_harbor_media.trim() !== "", {
    path: ["safe_harbor_media"],
    message: "An announcement needs a recording to play",
  })
  // The server's 422 for a window outside the calling hours (ACL-237), on the field it names.
  .superRefine((v, ctx) => {
    if (!HHMM.test(v.calling_window_start) || !HHMM.test(v.calling_window_end)) return;
    const problem = windowProblem(v.calling_window_start, v.calling_window_end);
    if (problem) ctx.addIssue({ code: "custom", path: [problem.field], message: problem.message });
  })
  // The server's 422 for a dial mode with no cadence (ADR-067), so the form refuses to submit it.
  .refine((v) => !needsCadence(v), { path: ["cadence_id"], message: NEEDS_CADENCE });

export function CampaignDialing({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const toast = useToast();
  const update = useUpdateCampaign();
  const { data: cadences } = useCadences();
  const { data: dids } = useDids({ tenant_id: campaign.tenant_id });
  // Trunks are admin-and-above in core-api, so a supervisor never asks for them.
  const { data: gateways } = useGateways({ enabled: canManage });
  const [formError, setFormError] = React.useState<string | null>(null);

  const form = useForm<DialingValues>({
    resolver: zodResolver(schema),
    defaultValues: dialingValues(campaign),
  });
  const { errors, isDirty } = form.formState;

  // A refetch — after a save, or on focus — re-seeds the form, unless somebody is mid-edit: their
  // unsaved changes are theirs to save or discard, not a background refresh's to throw away.
  React.useEffect(() => {
    if (!form.formState.isDirty) form.reset(dialingValues(campaign));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  const values = form.watch();
  const set = <K extends keyof DialingValues>(key: K, value: DialingValues[K]) =>
    form.setValue(key, value as never, { shouldDirty: true, shouldValidate: true });

  const onSubmit = form.handleSubmit(async (submitted) => {
    setFormError(null);
    const changes = dialingChanges(submitted, campaign) as CampaignSettingsInput;
    if (Object.keys(changes).length === 0) {
      form.reset(submitted);
      return;
    }
    try {
      const saved = await update.mutateAsync({ id: campaign.id, ...changes });
      form.reset(dialingValues(saved));
      toast.success("Dialing saved", campaign.name);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the dialing settings.");
    }
  });

  const modes: string[] = (DIAL_MODES as readonly string[]).includes(campaign.dial_mode)
    ? [...DIAL_MODES]
    : [...DIAL_MODES, campaign.dial_mode];
  const hours = windowNote(
    values.calling_window_start,
    values.calling_window_end,
    values.respect_lead_timezone,
  );
  const choices = callerIdChoices(dids ?? [], campaign.tenant_id, campaign.caller_id_number);
  const stateZone = `The campaign's state${campaign.state ? ` (${campaign.state})` : ""}`;
  // A retired cadence is offered only while the campaign still runs it, so it can be seen and
  // replaced; nobody attaches a retired one.
  const offered = (cadences ?? []).filter((c) => c.active || c.id === campaign.cadence_id);
  const chosen = offered.find((c) => c.id === values.cadence_id);
  // The cadence field says the server's rule, rather than "required": the rule only holds while
  // the dialer is on, and it is the error the moment a mode is chosen with nothing to run.
  const cadenceProblem = needsCadence(values) ? NEEDS_CADENCE : undefined;
  const cadenceHint =
    values.cadence_id === null
      ? "A campaign that dials needs a cadence."
      : chosen
        ? chosen.current_version
          ? bodySummary(chosen.current_version.body)
          : "No version to run yet."
        : undefined;

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      {formError && <Callout tone="danger">{formError}</Callout>}

      <fieldset disabled={!canManage} className="min-w-0 space-y-4">
        <Card>
          <CardHeader title="Dialer" description="Whether the dialer calls this campaign's leads, and how." />
          <CardBody className="space-y-4">
            <Field
              label="Cadence"
              htmlFor="campaign-cadence"
              error={cadenceProblem}
              hint={cadenceHint}
              action={
                <Link to="/cadences" className="text-xs text-text-muted hover:text-text hover:underline">
                  All cadences
                </Link>
              }
            >
              <Select
                value={values.cadence_id === null ? NONE : String(values.cadence_id)}
                onValueChange={(id) => set("cadence_id", id === NONE ? null : Number(id))}
              >
                <SelectTrigger id="campaign-cadence" className="sm:w-96">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {offered.map((c) => (
                    <SelectItem
                      key={c.id}
                      value={String(c.id)}
                      description={c.current_version ? bodySummary(c.current_version.body) : "No version to run yet"}
                    >
                      {c.name}
                      {!c.active && " (retired)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {campaign.cadence?.problem && <Callout tone="danger">{campaign.cadence.problem}</Callout>}
            {/* `?? []` for the minutes between deploying this console and the core-api that always
                sends the list: a missing field must not take the whole tab down with it. */}
            {(campaign.cadence_warnings ?? []).map((w) => (
              <Callout key={w} tone="warning">{w}</Callout>
            ))}

            <fieldset className="min-w-0 space-y-2">
              <legend className="mb-2 text-xs font-medium text-text">Mode</legend>
              <div className="grid gap-2 md:grid-cols-3">
                {modes.map((mode) => (
                  <Choice
                    key={mode}
                    name="dial-mode"
                    checked={values.dial_mode === mode}
                    onSelect={() => set("dial_mode", mode)}
                    title={dialModeText(mode).label}
                    description={dialModeText(mode).description}
                  />
                ))}
              </div>
            </fieldset>

            {values.dial_mode !== "off" && campaign.status !== "active" && (
              <Callout tone="warning">
                The campaign is {statusText(campaign.status).label.toLowerCase()}, and the dialer calls
                a campaign only while it is Active. The status is at the top of the page.
              </Callout>
            )}

            <Field
              label="Priority"
              htmlFor="dial-priority"
              required
              error={errors.dial_priority?.message}
              hint="When a rep works more than one dialing campaign, the one with the higher priority gives them their next lead. Campaigns on the same priority take turns. A campaign with nothing due passes the rep to the next."
            >
              <Input
                id="dial-priority"
                type="number"
                inputMode="numeric"
                className="sm:max-w-32"
                invalid={Boolean(errors.dial_priority)}
                {...form.register("dial_priority", { valueAsNumber: true })}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Ring a lead for"
                htmlFor="dial-timeout"
                required
                error={errors.dial_timeout_seconds?.message}
                hint={`Seconds before the dialer gives up on an unanswered call. At least ${MIN_DIAL_TIMEOUT_SECONDS}, the safe harbor's ring.`}
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  invalid={Boolean(errors.dial_timeout_seconds)}
                  {...form.register("dial_timeout_seconds", { valueAsNumber: true })}
                />
              </Field>
              <Field
                label="Preview offer lasts"
                htmlFor="preview-timeout"
                required
                error={errors.preview_timeout_seconds?.message}
                hint="Seconds a rep has to choose Dial or Skip before the lead goes back. Preview only."
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  invalid={Boolean(errors.preview_timeout_seconds)}
                  {...form.register("preview_timeout_seconds", { valueAsNumber: true })}
                />
              </Field>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Calling hours" description="When a lead may be called, and whose clock decides." />
          <CardBody className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="From" htmlFor="window-start" error={errors.calling_window_start?.message}>
                <Input
                  type="time"
                  invalid={Boolean(errors.calling_window_start)}
                  {...form.register("calling_window_start")}
                />
              </Field>
              <Field label="Until" htmlFor="window-end" error={errors.calling_window_end?.message}>
                <Input
                  type="time"
                  invalid={Boolean(errors.calling_window_end)}
                  {...form.register("calling_window_end")}
                />
              </Field>
            </div>
            {hours.outsideTcpa ? (
              <Callout tone="warning">
                {hours.text} That reaches outside 8 am to 9 pm on the called person's clock, the
                TCPA's calling hours.
              </Callout>
            ) : (
              <p className="text-xs text-text-muted">{hours.text}</p>
            )}

            <SwitchRow
              id="respect-lead-timezone"
              title="Call on each lead's own clock"
              description={
                values.respect_lead_timezone
                  ? "A lead's time zone, or their state's, decides. The zone below is for leads with neither."
                  : "Every lead is called on the zone below."
              }
              checked={values.respect_lead_timezone}
              onChange={(checked) => set("respect_lead_timezone", checked)}
            />

            <Field
              label={values.respect_lead_timezone ? "Otherwise" : "Time zone"}
              htmlFor="default-timezone"
              hint="A lead no zone can be found for is not called."
            >
              <Select
                value={values.default_timezone || NONE}
                onValueChange={(zone) => set("default_timezone", zone === NONE ? "" : zone)}
              >
                <SelectTrigger id="default-timezone" className="sm:w-80">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{stateZone}</SelectItem>
                  {US_TIMEZONES.map((t) => (
                    <SelectItem key={t.zone} value={t.zone} description={t.zone}>
                      {t.label}
                    </SelectItem>
                  ))}
                  {values.default_timezone &&
                    !US_TIMEZONES.some((t) => t.zone === values.default_timezone) && (
                      <SelectItem value={values.default_timezone}>{values.default_timezone}</SelectItem>
                    )}
                </SelectContent>
              </Select>
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="When no rep is free"
            description="A lead answered and no rep reached them in time. The call is abandoned, and this is what the lead hears."
          />
          <CardBody className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Abandon after"
                htmlFor="abandon-after"
                required
                error={errors.abandon_after_seconds?.message}
                hint="Seconds from the lead's answer. The TCPA's safe harbor is 2."
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  invalid={Boolean(errors.abandon_after_seconds)}
                  {...form.register("abandon_after_seconds", { valueAsNumber: true })}
                />
              </Field>
              <Field
                label="Then"
                htmlFor="safe-harbor-action"
                hint={safeHarborText(values.safe_harbor_action).description}
              >
                <Select
                  value={values.safe_harbor_action}
                  onValueChange={(action) => set("safe_harbor_action", action)}
                >
                  <SelectTrigger id="safe-harbor-action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SAFE_HARBOR_ACTIONS.map((action) => (
                      <SelectItem key={action} value={action}>
                        {safeHarborText(action).label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            {values.safe_harbor_action === "announcement" && (
              <Field
                label="Recording"
                htmlFor="safe-harbor-media"
                required
                error={errors.safe_harbor_media?.message}
                hint="What the lead hears before the call ends: who called, and how to call back."
              >
                <AudioPicker
                  value={values.safe_harbor_media}
                  onChange={(v) => set("safe_harbor_media", v)}
                  emptyLabel="No recording chosen"
                />
              </Field>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Caller ID and trunks"
            description="The number the dialer's calls present, and the carriers they leave on."
          />
          <CardBody className="space-y-4">
            <Field
              label="Caller ID"
              htmlFor="campaign-caller-id"
              hint="Every call the dialer places for this campaign presents it. Left to the rep, a call presents the rep's own number, then their domain's default."
            >
              <Select
                value={values.caller_id_number || NONE}
                onValueChange={(number) => set("caller_id_number", number === NONE ? "" : number)}
              >
                <SelectTrigger id="campaign-caller-id" className="sm:w-96">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Left to the rep</SelectItem>
                  {choices.map((choice) => (
                    <SelectItem key={choice.number} value={choice.number}>
                      <span className="font-mono">{formatPhoneNumber(choice.number)}</span>
                      {choice.held ? (
                        choice.description && (
                          <span className="ml-1.5 text-text-muted">{choice.description}</span>
                        )
                      ) : (
                        <span className="ml-1.5 text-warning-11">no longer held</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <SwitchRow
              id="campaign-rep-number-caller-id"
              title="A rep with their own number on this campaign presents it"
              description="So a lead who calls back reaches the rep who called them. Reps without one present the caller ID above."
              checked={values.rep_number_caller_id}
              onChange={(checked) => set("rep_number_caller_id", checked)}
            />

            <Trunks
              chosen={values.outbound_gateway_ids}
              gateways={gateways}
              canManage={canManage}
              onChange={(ids) => set("outbound_gateway_ids", ids)}
            />
          </CardBody>
        </Card>
      </fieldset>

      {canManage && (
        <div className="sticky bottom-0 z-10 flex items-center justify-end gap-2 rounded-lg border border-border bg-surface px-4 py-3 shadow-xs">
          <span className="mr-auto text-xs text-text-muted">
            {isDirty ? "Unsaved changes" : "Saved"}
          </span>
          <Button
            type="button"
            variant="outline"
            disabled={!isDirty || update.isPending}
            onClick={() => {
              setFormError(null);
              form.reset(dialingValues(campaign));
            }}
          >
            Discard
          </Button>
          <Button type="submit" variant="default" loading={update.isPending} disabled={!isDirty}>
            Save dialing
          </Button>
        </div>
      )}
    </form>
  );
}

/**
 * The trunks, in order. Only an admin can read a trunk, so a supervisor is told how many are chosen
 * and not which.
 */
function Trunks({
  chosen, gateways, canManage, onChange,
}: {
  chosen: number[];
  gateways: Gateway[] | undefined;
  canManage: boolean;
  onChange: (ids: number[]) => void;
}) {
  const heading = <p className="text-xs font-medium text-text">Trunks</p>;
  if (!canManage) {
    return (
      <div className="space-y-1">
        {heading}
        <p className="text-xs text-text-muted">
          {chosen.length === 0
            ? "None chosen: each call leaves the way its rep's domain routes outbound calls."
            : `${chosen.length} chosen, tried in order.`}
        </p>
      </div>
    );
  }

  const byId = new Map((gateways ?? []).map((g) => [g.id, g]));
  const available = (gateways ?? []).filter(
    (g) => g.profile_name === OUTBOUND_PROFILE && !chosen.includes(g.id),
  );

  return (
    <div className="space-y-2">
      <div>
        {heading}
        <p className="text-xs text-text-muted">
          {chosen.length === 0
            ? "None chosen: each call leaves the way its rep's domain routes outbound calls."
            : "Tried in order, the first one first."}
        </p>
      </div>

      {chosen.length > 0 && (
        <ol className="space-y-1.5">
          {chosen.map((id, i) => {
            const g = byId.get(id);
            return (
              <li
                key={id}
                className={
                  g
                    ? "flex items-center gap-2 rounded-md border border-border px-3 py-2"
                    : "flex items-center gap-2 rounded-md border border-danger-6 px-3 py-2"
                }
              >
                <span className="w-4 text-xs tabular-nums text-text-subtle">{i + 1}</span>
                <span className="min-w-0 flex-1">
                  {g ? (
                    <>
                      <span className="text-xs font-medium text-text">{g.name}</span>
                      <span className="ml-1.5 font-mono text-xs text-text-muted">{g.realm}</span>
                      {!g.enabled && (
                        <Badge tone="warning" size="sm" className="ml-1.5">
                          Disabled
                        </Badge>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-danger-11">
                      {gateways ? `Gateway ${id} no longer exists.` : `Gateway ${id}`}
                    </span>
                  )}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Move ${g?.name ?? `gateway ${id}`} up`}
                  disabled={i === 0}
                  onClick={() => onChange(moveItem(chosen, i, -1))}
                >
                  <ArrowUp />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Move ${g?.name ?? `gateway ${id}`} down`}
                  disabled={i === chosen.length - 1}
                  onClick={() => onChange(moveItem(chosen, i, 1))}
                >
                  <ArrowDown />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove ${g?.name ?? `gateway ${id}`}`}
                  onClick={() => onChange(chosen.filter((x) => x !== id))}
                >
                  <X />
                </Button>
              </li>
            );
          })}
        </ol>
      )}

      {available.length > 0 && (
        <ul className="space-y-1.5">
          {available.map((g) => (
            <li key={g.id} className="flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2">
              <span className="min-w-0 flex-1">
                <span className="text-xs font-medium text-text">{g.name}</span>
                <span className="ml-1.5 font-mono text-xs text-text-muted">{g.realm}</span>
                {!g.enabled && (
                  <Badge tone="warning" size="sm" className="ml-1.5">
                    Disabled
                  </Badge>
                )}
              </span>
              <Button type="button" variant="outline" size="sm" onClick={() => onChange([...chosen, g.id])}>
                <Plus />
                Add
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Choice({
  name, checked, onSelect, title, description,
}: {
  name: string;
  checked: boolean;
  onSelect: () => void;
  title: string;
  description: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border px-3 py-2.5 has-[:checked]:border-accent-8 has-[:checked]:bg-accent-2 has-[:disabled]:cursor-default">
      <input
        type="radio"
        name={name}
        className="mt-0.5 accent-accent-9"
        checked={checked}
        onChange={onSelect}
      />
      <span>
        <span className="block text-xs font-medium text-text">{title}</span>
        <span className="block text-xs text-text-muted">{description}</span>
      </span>
    </label>
  );
}

function SwitchRow({
  id, title, description, checked, onChange,
}: {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
      <div>
        <p id={`${id}-label`} className="text-xs font-medium text-text">
          {title}
        </p>
        <p className="text-xs text-text-muted">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} aria-labelledby={`${id}-label`} />
    </div>
  );
}
